import { Form, Formik } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createRequiredDoc } from "@/features/required-docs/api";
import { ApiError } from "@/lib/api";

const schema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  notes: Yup.string(),
  due_date: Yup.string(),
});

interface AddRequiredDocValues {
  name: string;
  notes: string;
  due_date: string;
}

const initialValues: AddRequiredDocValues = { name: "", notes: "", due_date: "" };

interface AddRequiredDocDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  /** Called after a successful create so the parent can refresh its list. */
  onCreated: () => void;
}

export function AddRequiredDocDialog({
  open,
  onOpenChange,
  clientId,
  onCreated,
}: AddRequiredDocDialogProps) {
  async function handleSubmit(values: AddRequiredDocValues) {
    try {
      await createRequiredDoc(clientId, {
        name: values.name.trim(),
        notes: values.notes.trim() || undefined,
        due_date: values.due_date || undefined,
      });
      toast.success("Required document added");
      onCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add required document.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add required document</DialogTitle>
          <DialogDescription>
            Request a document from this client. It starts as Pending.
          </DialogDescription>
        </DialogHeader>
        <Formik<AddRequiredDocValues>
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Document name" placeholder="e.g. W-2 (2024)" autoFocus />
              <FormField name="notes" label="Notes" placeholder="Optional details for the client" />
              <FormField name="due_date" label="Due date" type="date" />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Adding…" : "Add document"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
